import { Injectable } from '@angular/core';
import {
    AcknowledgeReferralRequest,
    BodyPart,
    ControlCentreApiClient,
    EntityType,
    GetIntegratedProtoReferral,
    GetReferral,
    GetReferralDetails,
    GetReferralLinkingDetails,
    GetReferralPrior,
    Modality,
    PatchReferral,
    PostReferral,
    PostReferralPrior,
    PutReferralItem,
    ReferralStatus,
    UpsertNotification,
    UrgencyType
} from '../api-module/control-centre-api-client';
import { ConfigService } from '../services/config.service';
import { SnackbarService } from '../services/snackbar.sevice';
import { Constants } from '../models/constants';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ReferralDeferralResponse, statusesAllowingAcknowledgement, ViewReferralConfirmationInjectData } from './referrals.model';
import { DialogViewReferralConfirmationComponent } from './dialog-view-referral-confirmation/dialog-view-referral-confirmation.component';
import { MessageBoxIcon } from '../messagebox/messagebox.model';
import {
    HubSubscription,
    HubSubscriptionAction,
    HubSubscriptionEventFnPair,
    SignalRReferralsService
} from '../services/signal-r-referrals.service';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root'
})
export class ReferralsService {
    constructor(
        private readonly controlCentreApiClient: ControlCentreApiClient,
        private readonly configService: ConfigService,
        private readonly snackbarService: SnackbarService,
        private readonly dialog: MatDialog,
        private readonly signalRReferralsService: SignalRReferralsService
    ) {}

    referralsCount$ = new Subject<number>();
    checkReferralsAwaitingAcknowledgement$ = new BehaviorSubject(null);
    hasReferralsAwaitingAcknowledgement$ = new BehaviorSubject(false);

    getReferralList(data: {
        patientExternalId?: string | null;
        updatedAtFrom?: string | null;
        updatedAtTo?: string | null;
        orgCode: string;
        siteIds?: number[] | null;
        createdAtFrom?: string;
        createdAtTo?: string | null;
        status?: ReferralStatus[] | null;
        urgency?: UrgencyType[] | null;
        modality?: string[] | null;
    }): Observable<GetReferral[]> {
        return this.controlCentreApiClient
            .referrals_GetReferralList(
                data.orgCode,
                data.createdAtFrom,
                data.createdAtTo,
                data.patientExternalId,
                data.siteIds,
                data.updatedAtFrom,
                data.updatedAtTo,
                data.status,
                data.urgency,
                data.modality
            )
            .pipe(timeout(Constants.HttpTimeoutMseconds))
            .pipe(
                map(paginatedResponse => paginatedResponse.items),
                catchError(
                    this.getCatchErrorHandler({
                        403: `You do not have permission to view these referrals (orgCode: ${data.orgCode}, siteIds: ${data.siteIds})`
                    })
                ),
                map(items => items ?? [])
            );
    }

    getReferral(id: number): Observable<GetReferralDetails> {
        return this.controlCentreApiClient
            .referrals_GetReferral(id)
            .pipe(
                timeout(Constants.HttpTimeoutMseconds),
                catchError(this.getCatchErrorHandler({ 403: `You do not have permission to view this referral (${id})` }))
            );
    }

    getModalities(): Observable<Modality[]> {
        return this.controlCentreApiClient.modalities_GetModalities().pipe(
            shareReplay(),
            timeout(Constants.HttpTimeoutMseconds),
            map(paginatedResponseOfModality => paginatedResponseOfModality.items)
        );
    }

    getBodyParts(): Observable<BodyPart[]> {
        return this.controlCentreApiClient.bodyParts_GetBodyParts().pipe(
            shareReplay(),
            timeout(Constants.HttpTimeoutMseconds),
            map(paginatedResponseOfBodyPart => paginatedResponseOfBodyPart.items)
        );
    }

    acknowledgeReferral(id: number, request: AcknowledgeReferralRequest) {
        return this.controlCentreApiClient.referrals_AcknowledgeReferral(id, request).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    getReferralLinkingDetails(accessionNumber: string): Observable<GetReferralLinkingDetails> {
        return this.controlCentreApiClient
            .referrals_GetReferralLinkingDetails(accessionNumber)
            .pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    postReferral(postReferral: PostReferral): Observable<void> {
        return this.controlCentreApiClient.referrals_PostReferral(postReferral).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    patchReferral(referralId: number, patchReferral: PatchReferral): Observable<void> {
        return this.controlCentreApiClient.referrals_PatchReferral(referralId, patchReferral).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    putReferralItems(referralId: number, putReferralItems: PutReferralItem[]): Observable<void> {
        return this.controlCentreApiClient
            .referralItems_PutReferralItems(referralId, putReferralItems)
            .pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    postReferralPriors(referralId: number, priors: PostReferralPrior[]): Observable<GetReferralPrior[]> {
        return this.controlCentreApiClient.referralPriors_PostPriors(referralId, priors).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    deletePrior(referralId: number, priorId: number) {
        return this.controlCentreApiClient.referralPriors_DeletePrior(referralId, priorId).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    postClinicalDiscussion(referralId: number): Observable<void> {
        return this.controlCentreApiClient.clinicalDiscussions_Post(referralId).pipe(timeout(Constants.HttpTimeoutMseconds));
    }

    getIntegratedProtoReferrals(
        orgCode: string,
        siteIds: number[],
        searchString?: string | null
    ): Observable<GetIntegratedProtoReferral[]> {
        return this.controlCentreApiClient
            .integratedProtoReferrals_GetIntegratedProtoReferrals(orgCode, siteIds, searchString)
            .pipe(timeout(Constants.HttpTimeoutMseconds))
            .pipe(
                map(paginatedResponse => paginatedResponse.items),
                catchError(
                    this.getCatchErrorHandler({
                        403: `You do not have permission to view these referrals (orgCode: ${orgCode}, siteIds: ${siteIds})`
                    })
                ),
                map(items => items ?? [])
            );
    }

    getCatchErrorHandler = (errorMessages: { [errorCode: number]: string }) => error => {
        if (errorMessages[error.status] !== undefined) {
            this.snackbarService.openSnackBar(errorMessages[error.status], SnackbarService.SNACK_BAR_CLASS_ALERT);
        } else {
            this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
        }

        return of(null); // Not to rethrow exception to avoid breaking subscriptions.
    };

    openViewReferralConfirmationPopup(data: {
        title: string;
        message: string;
        icon: MessageBoxIcon;
        showInput: boolean;
        buttonTextForConfirm?: string;
    }): Observable<ReferralDeferralResponse> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = new ViewReferralConfirmationInjectData({
            title: data.title,
            message: data.message,
            icon: data.icon,
            showInput: data.showInput,
            buttonTextForConfirm: data.buttonTextForConfirm
        });
        dialogConfig.panelClass = ['general-popup-container', 'draggable-dialog-container'];
        dialogConfig.backdropClass = 'general-dialog-backdrop';
        dialogConfig.maxWidth = '800px';
        dialogConfig.width = '50vw';
        dialogConfig.autoFocus = true;

        const dialogRef = this.dialog.open(DialogViewReferralConfirmationComponent, dialogConfig);
        return dialogRef.afterClosed();
    }

    hasUnacknowledgedReferrals(orgCode: string, siteIds?: number[]): Observable<boolean> {
        const dateFrom = DateTime.now().minus({ days: 60 });
        return this.getReferralList({
            orgCode,
            siteIds,
            status: statusesAllowingAcknowledgement,
            createdAtFrom: dateFrom.toISO()
        }).pipe(map(items => items.length > 0));
    }

    /**
     * A method to allow a component to subscribe to Referral change notification.
     * @param subscriptionId The id for the new subscription.
     * @param orgCode The orgCode for subscription.
     * @param siteIds The list of siteId for subscription.
     * @param onUpsertFn A function for event OnReferralUpsert.
     * @param onDeleteFn A function for event OnReferralDelete.
     * @returns A newly created HubSubscription.
     */
    async subscribeToReferralUpdates(
        subscriptionId: string,
        orgCode: string | undefined,
        siteIds: number[] | undefined,
        onUpsertFunction: (notification: UpsertNotification) => void,
        onDeleteFunction: (entityId: string, entityType: EntityType) => void
    ) {
        const eventPairs = [];
        eventPairs.push(new HubSubscriptionEventFnPair('OnReferralUpsert', onUpsertFunction));
        eventPairs.push(new HubSubscriptionEventFnPair('OnReferralDelete', onDeleteFunction));
        const newSub = new HubSubscription(
            subscriptionId,
            orgCode,
            siteIds,
            'SubscribeToReferrals',
            'UnsubscribeFromReferrals',
            eventPairs
        );
        const action = new HubSubscriptionAction(false, newSub, undefined);
        await this.signalRReferralsService.addAction(action);
        return newSub;
    }

    /**
     * A method to unsubscribe from server. Not async/await so component doesn't need to stop and wait for result.
     * @param {string | undefined} subscriptionId The id of the HubSubscription to be unsubscribed.
     */
    async unsubscribeFromReferralUpdates(subscriptionId: string | undefined) {
        if (subscriptionId) {
            const action = new HubSubscriptionAction(true, undefined, subscriptionId);
            await this.signalRReferralsService.addAction(action);
        }
    }
}
